<script setup lang="ts">
import { ref, computed } from "vue";
import type { user } from "~/types/general";
import {
  MagnifyingGlassIcon,
  HeartIcon,
  ShoppingCartIcon,
  Bars3BottomRightIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import IconStar from "~/assets/icons/iconStar.vue";
const { averageRating, reviewCount, fetchTestimony } = useTestimony();
const { isLoggedIn, user, logout } = useSanctum<user>();
const isMenuOpen = ref(false);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const logoutUser = () => {
  logout();
  toggleMenu();
};

const tours = useTourStore();
const allTours = computed(() => Array.isArray(tours.getTours) ? tours.getTours : []);

const searchQuery = ref("");
const isSearchOpen = ref(false);
const suggestions = ref<string[]>([]);
const isLoading = ref(false);

// const allItems = [
//   "Ubud Gorilla Face ATV and Ayung Rafting Trip with Meal",
//   "Mount Batur Sunset Jeep Tour",
//   "Blacklava Kintamani Jeep Tour",
//   "Mount Batur Sunrise Jeep Tour",
//   "Horse Riding Combo Experience in Bali",
//   "Blue Lagoon Snorkeling & Waterfall Tour with Lunch ",
//   "Ubud: River Cave Tubing Adventure with Lunch",
//   "3 Days Bali Highlights Private Tour",
// ]; // Example data

const filteredSuggestions = computed(() =>
  searchQuery.value.length >= 3
    ? allTours.value.filter((tour) =>
      tour.title.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
    : []
);

watch(searchQuery, (newQuery) => {
  if (newQuery.length >= 3) {
    isLoading.value = true;
    setTimeout(() => {
      isLoading.value = false;
    }, 1000);
  } else {
    isLoading.value = false;
  }
});

const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value;
};

const closeSearch = () => {
  isSearchOpen.value = false;
  searchQuery.value = "";
};

// handle rate
// watch(filteredSuggestions, (suggestions) => {
//   const allTestimonies = suggestions.flatMap(t => t.testimonies || []);
//   fetchTestimony(allTestimonies);
// });
</script>
<template>
  <nav class="flex items-center justify-between px-[18px] pt-2.5">
    <NuxtLink to="/">
      <div class="w-[150px] md:w-[250px] h-[50px] overflow-hidden relative">
        <img src="/images/Bali_Xperiences_Logo.png" alt="Logo"
          class="absolute object-cover w-full h-full -left-4 md:-left-8" />
      </div>
    </NuxtLink>
    <ul class="flex gap-x-6">
      <li>
        <button @click="toggleSearch">
          <MagnifyingGlassIcon class="w-6 h-6 text-white" />
        </button>
      </li>
      <li>
        <NuxtLink to="/wishlist">
          <HeartIcon class="w-6 h-6 text-white heroicon-sw-1" />
        </NuxtLink>
      </li>
      <li>
        <NuxtLink to="/cart">
          <ShoppingCartIcon class="w-6 h-6 text-white heroicon-sw-1" />
        </NuxtLink>
      </li>
      <li id="open-menu" @click="toggleMenu">
        <NuxtLink>
          <Bars3BottomRightIcon class="w-6 h-6 text-white heroicon-sw-1" />
        </NuxtLink>
      </li>
    </ul>
  </nav>
  <Teleport to="body">
    <div v-if="isSearchOpen" class="fixed inset-0 z-[9999] flex p-3 md:p-10 justify-center bg-black bg-opacity-50">
      <div class="w-full p-4 mt-10 overflow-y-auto bg-white rounded shadow-lg">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold">Search</h3>
          <button @click="closeSearch" aria-label="close search">
            <XMarkIcon class="w-6 h-6 text-gray-500" />
          </button>
        </div>
        <input v-model="searchQuery" type="text" placeholder="Type to search..." class="w-full p-2 border rounded" />

        <ul v-if="isLoading" class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3 lg:grid-cols-4">
          <li v-for="index in 4" :key="index" class="p-2">
            <div class="flex flex-col w-full gap-4 mt-10">
              <div class="w-full h-32 skeleton"></div>
              <div class="h-4 skeleton w-28"></div>
              <div class="w-full h-4 skeleton"></div>
              <div class="w-full h-4 skeleton"></div>
            </div>
          </li>
        </ul>

        <ul v-else-if="filteredSuggestions.length" class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3 lg:grid-cols-4">
          <li v-for="(suggestion, index) in filteredSuggestions" :key="index"
            class="p-2 rounded-lg cursor-pointer hover:bg-gray-100">
            <NuxtLink :to="'/tour/' + suggestion.slug" class="block">
              <div class="relative w-full mb-4 overflow-hidden h-28 rounded-xl">
                <img :src="suggestion.photos[0].path" alt="" class="object-cover object-center w-full h-full" />
              </div>
              <div class="space-y-2 text-left">
                <div class="flex items-baseline">
                  <div class="flex gap-1">
                    <img v-for="n in 5" :key="n" :src="n <= Math.round(suggestion.testimonies.reduce((a, b) => a + b.rate, 0) / suggestion.testimonies.length)
                      ? '/icons/star.svg'
                      : '/images/star_outline.png'" alt="star" width="15" />
                  </div>
                  <span class="ml-2 text-xs text-gray-500">({{ suggestion.testimonies.length }} Reviews)</span>
                </div>
                <h4 class="">{{ suggestion.title }}</h4>
                <div>
                  <span class="mr-0.5 font-semibold text-secondary-80 md:font-medium">From US$ {{
                    suggestion.prices[suggestion.prices.length - 1]?.price ?? 0 }}</span>

                  <sup class="text-gray-500">per person</sup>
                </div>
              </div>
              <div>
              </div>
            </NuxtLink>
          </li>
        </ul>

        <p v-else-if="searchQuery.length >= 3" class="mt-4 text-center text-gray-500">
          No results found.
        </p>
      </div>
    </div>


  </Teleport>
  <Teleport to="body">
    <div id="mobile-menu" :class="{ '-left-full': !isMenuOpen, 'left-0': isMenuOpen }"
      class="fixed inset-0 z-[9999] flex h-screen w-full md:w-1/2 flex-col bg-white duration-300">
      <div class="w-full">
        <div class="flex items-center justify-end py-2 pr-4 text-white bg-primary">
          <button aria-label="close menu" id="close-menu" class="p-2" @click="toggleMenu">
            <XMarkIcon class="w-6 h-6 text-white heroicon-sw-1" />
          </button>
        </div>
      </div>
      <div class="pt-4 mx-4">
        <h3 class="mb-3 text-lg font-bold">Profile</h3>
        <p class="mb-4">
          Welcome, <span>{{ user?.name ?? "Guess" }} </span>
        </p>
        <ul v-if="isLoggedIn" class="flex flex-col space-y-4">
          <li class="ml-0 list-none menu-item">
            <NuxtLink to="/my-bookings">My Bookings </NuxtLink>
          </li>
          <li class="ml-0 list-none menu-item">
            <button @click.prevent="logoutUser" class="text-gray-500">
              Log out
            </button>
          </li>
        </ul>
        <ul v-else class="flex flex-col space-y-4">
          <li class="ml-0 list-none menu-item">
            <NuxtLink to="/login">Log in </NuxtLink>
          </li>
          <li class="ml-0 list-none menu-item">
            <NuxtLink to="/register">Register </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </Teleport>
</template>
